/* eslint-disable no-console */
import { v4 as uuidv4 } from 'uuid';

import { debugLog, finleyAssert } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { StoredGraphState } from './GraphContext';

type Node = StoredGraphState['nodes'][0];

const getTargetNodes = (
  graph: StoredGraphState,
  sourceNodeId: string,
): Node[] => {
  const edges = graph.edges.filter((edge) => edge.source === sourceNodeId);

  const targetNodeIds = edges.map((edge) => edge.target);

  return graph.nodes.filter((node) =>
    targetNodeIds.includes(node.metadata.tapeBuilderNodeId),
  );
};

const removeEdges = ({
  graph,
  edgesToRemove,
}: {
  graph: StoredGraphState;
  edgesToRemove: {
    source: string;
    target: string;
  }[];
}): StoredGraphState => {
  const newEdges = graph.edges.filter(
    (edge) =>
      !edgesToRemove.some(
        (edgeToRemove) =>
          edgeToRemove.source === edge.source &&
          edgeToRemove.target === edge.target,
      ),
  );

  return {
    ...graph,
    edges: newEdges,
  };
};

const getSourceNodes = (
  graph: StoredGraphState,
  targetNodeId: string,
): Node[] => {
  const edges = graph.edges.filter((edge) => edge.target === targetNodeId);

  const sourceNodeIds = edges.map((edge) => edge.source);

  return graph.nodes.filter((node) =>
    sourceNodeIds.includes(node.metadata.tapeBuilderNodeId),
  );
};

const replaceXlsx2Csv = ({
  graph,
  oldXls2CsvNode,
  newXls2CsvNode,
  oldSanitizeNode,
}: {
  graph: StoredGraphState;
  oldXls2CsvNode: Node;
  newXls2CsvNode: Node;
  oldSanitizeNode: Node;
}): {
  graph: StoredGraphState;
  errors: string[];
} => {
  // initially the graph has
  // rawNode => oldXls2CsvNode => tapeNodeA => oldSanitizeNode => tapeNodeB
  const rawNodes = getSourceNodes(
    graph,
    oldXls2CsvNode.metadata.tapeBuilderNodeId,
  );
  if (rawNodes.length !== 1) {
    return {
      graph,
      errors: [
        `replaceXlsx2Csv: ${oldXls2CsvNode.metadata.tapeBuilderNodeId} Expected just one raw node`,
      ],
    };
  }

  const rawNode = rawNodes[0];

  // find tapeNodeA
  const tapeNodeAs = getTargetNodes(
    graph,
    oldXls2CsvNode.metadata.tapeBuilderNodeId,
  );

  if (tapeNodeAs.length !== 1) {
    return {
      graph,
      errors: [
        `replaceXlsx2Csv: ${oldXls2CsvNode.metadata.tapeBuilderNodeId} Expected just one tapeNodeA`,
      ],
    };
  }

  const tapeNodeA = tapeNodeAs[0];

  // find tapeNodeB
  const tapeNodeBs = getTargetNodes(
    graph,
    oldSanitizeNode.metadata.tapeBuilderNodeId,
  );

  if (tapeNodeBs.length !== 1) {
    return {
      graph,
      errors: [
        `replaceXlsx2Csv: ${oldXls2CsvNode.metadata.tapeBuilderNodeId} Expected just one tapeNodeB`,
      ],
    };
  }

  // create a new stack node.
  // this will replace sanitize operation with no-op stack operation.
  const noOpStackNode: Node = {
    metadata: {
      tapeBuilderNodeId: uuidv4(),
      tapeBuilderNodeType: 'operation_stack',
    },
    configuration: {
      operation: 'stack',
      ensureUniqueFields: [],
    },
  };

  const tapeNodeB = tapeNodeBs[0];

  // remove edges
  const edgesToRemove = [
    {
      // rawNode => oldXls2CsvNode
      source: rawNode.metadata.tapeBuilderNodeId,
      target: oldXls2CsvNode.metadata.tapeBuilderNodeId,
    },
    {
      // oldXls2CsvNode => tapeNodeA
      source: oldXls2CsvNode.metadata.tapeBuilderNodeId,
      target: tapeNodeA.metadata.tapeBuilderNodeId,
    },
    {
      // tapeNodeA => oldSanitizeNode
      source: tapeNodeA.metadata.tapeBuilderNodeId,
      target: oldSanitizeNode.metadata.tapeBuilderNodeId,
    },
    {
      // oldSanitizeNode => tapeNodeB
      source: oldSanitizeNode.metadata.tapeBuilderNodeId,
      target: tapeNodeB.metadata.tapeBuilderNodeId,
    },
  ];

  const newGraph = removeEdges({
    graph,
    edgesToRemove,
  });

  newGraph.edges.push(
    // rawNode => newXls2CsvNode
    {
      source: rawNode.metadata.tapeBuilderNodeId,
      target: newXls2CsvNode.metadata.tapeBuilderNodeId,
      id: uuidv4(),
    },

    // newXls2CsvNode => tapeNodeA
    {
      source: newXls2CsvNode.metadata.tapeBuilderNodeId,
      target: tapeNodeA.metadata.tapeBuilderNodeId,
      id: uuidv4(),
    },

    // tapeNodeA => noOpStackNode
    {
      source: tapeNodeA.metadata.tapeBuilderNodeId,
      target: noOpStackNode.metadata.tapeBuilderNodeId,
      id: uuidv4(),
    },

    // noOpStackNode => tapeNodeB
    {
      source: noOpStackNode.metadata.tapeBuilderNodeId,
      target: tapeNodeB.metadata.tapeBuilderNodeId,
      id: uuidv4(),
    },
  );

  // remove old nodes.
  const nodesToRemove = [oldXls2CsvNode, oldSanitizeNode];
  newGraph.nodes = newGraph.nodes.filter(
    (node) =>
      !nodesToRemove.some(
        (nodeToRemove) =>
          node.metadata.tapeBuilderNodeId ===
          nodeToRemove.metadata.tapeBuilderNodeId,
      ),
  );

  // add new nodes
  newGraph.nodes.push(newXls2CsvNode);
  newGraph.nodes.push(noOpStackNode);

  return {
    graph: newGraph,
    errors: [],
  };
};

export const migrateGraph = (
  previousGraph: StoredGraphState,
): {
  graph: StoredGraphState | null;
  errors: string[];
} => {
  let { nodes, edges } = previousGraph;

  // check if xlsx2csv node exists
  const xlsx2csvNodes = nodes.filter(
    (node) => node.metadata.tapeBuilderNodeType === 'operation_xlsx2csv',
  );

  if (xlsx2csvNodes.length === 0) {
    return {
      graph: previousGraph,
      errors: ['No xlsx2csv nodes found'],
    };
  }

  // we are going to handle simple case here.
  // xlsx2csv => tape (a) => => sanitize => tape (b)
  // xlsx2csv2 => tape (a) => noop (stack) => tape (b)
  // note we maintain same number of artifacts
  // just to make sure that any references to tape (b)
  // are still valid.

  const errors: string[] = [];
  xlsx2csvNodes.forEach((xlsx2csvNode) => {
    const nextNodes = getTargetNodes(
      { nodes, edges },
      xlsx2csvNode.metadata.tapeBuilderNodeId,
    );

    // xlsx2csv produces only 1 target.
    finleyAssert(
      nextNodes.length === 1,
      'Xlsx2csvOperation expected one target node',
    );

    const intermediateNode = nextNodes[0];

    const sanitizeNodes = getTargetNodes(
      { nodes, edges },
      intermediateNode.metadata.tapeBuilderNodeId,
    );

    finleyAssert(sanitizeNodes.length === 1, 'Expected one sanitize node');

    const sanitizeNode = sanitizeNodes[0];
    if (sanitizeNode.metadata.tapeBuilderNodeType !== 'operation_sanitize') {
      // there are some graphs where xlsx2csv is connected to preFilter. ignore those for now.
      // eslint-disable-next-line no-console
      errors.push(
        `Ignoring xlsx2csv[${xlsx2csvNode.metadata.tapeBuilderNodeId}] because its target is :${sanitizeNode.metadata.tapeBuilderNodeType}`,
      );

      return;
    }

    const result = TapeBuilderUI.generateXlsx2Csv2Config({
      sanitizeNode,
      xlsx2csvNode,
    });
    result.errors.forEach((error) => {
      errors.push(
        `Error in [${xlsx2csvNode.metadata.tapeBuilderNodeId}]: ${error}`,
      );
    });
    if (!result.config) {
      debugLog('No config found for', xlsx2csvNode.metadata.tapeBuilderNodeId);
      return;
    }

    const newId = uuidv4();

    const { graph: graphX, errors: nodeErrors } = replaceXlsx2Csv({
      graph: { nodes, edges },
      oldXls2CsvNode: xlsx2csvNode,
      newXls2CsvNode: {
        metadata: {
          tapeBuilderNodeId: newId,
          tapeBuilderNodeType: 'operation_xlsx2csv2',
        },
        configuration: result.config,
      },
      oldSanitizeNode: sanitizeNode,
    });

    nodeErrors.forEach((error) => {
      errors.push(
        `Error in [${xlsx2csvNode.metadata.tapeBuilderNodeId}]: ${error}`,
      );
    });

    nodes = graphX.nodes;
    edges = graphX.edges;
  });

  return { graph: { nodes, edges }, errors };
};
